import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Heading,
  VStack,
  HStack,
  Button,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Card,
  CardBody,
  Text,
  Badge,
  useColorModeValue,
  Alert,
  AlertIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Textarea,
  useToast,
  InputGroup,
  InputLeftElement,
  Select,
} from '@chakra-ui/react';
import { api } from '../services/api';

interface BookFile {
  id: number;
  bookId: number;
  typeId: number;
  link?: string;
  basePath?: string;
}

interface BookType {
  id: number;
  typeName: string;
  description?: string;
  contentType: 'PDF' | 'VIDEO' | 'IMAGE';
  _count?: {
    bookFiles: number;
  };
}

const CONTENT_TYPES = {
  PDF: 'PDF',
  VIDEO: 'VIDEO',
  IMAGE: 'IMAGE'
} as const;

type ContentType = typeof CONTENT_TYPES[keyof typeof CONTENT_TYPES];

interface FormData {
  typeName: string;
  description: string;
  contentType: ContentType;
}

const getContentTypeIcon = (type: string) => {
  switch (type) {
    case 'PDF':
      return '📄';
    case 'VIDEO':
      return '🎥';
    case 'IMAGE':
      return '🖼️';
    default:
      return '📁';
  }
};

const BookTypesPage = () => {
  const [bookTypes, setBookTypes] = useState<BookType[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingType, setEditingType] = useState<BookType | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeToDelete, setTypeToDelete] = useState<BookType | null>(null);
  
  const [formData, setFormData] = useState<FormData>({
    typeName: '',
    description: '',
    contentType: 'PDF'
  });

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDeleteModalOpen,
    onOpen: onDeleteModalOpen,
    onClose: onDeleteModalClose
  } = useDisclosure();
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.800');

  // Filter book types based on search term
  const filteredBookTypes = useMemo(() => {
    if (!searchTerm) return bookTypes;
    
    return bookTypes.filter((bookType) =>
      bookType.typeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (bookType.description && bookType.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [bookTypes, searchTerm]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await api.getBookTypes();
      setBookTypes(data);
    } catch (error) {
      console.error('Error loading book types:', error);
      toast({
        title: 'Error',
        description: 'Failed to load book types',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);

      if (editingType) {
        await api.updateBookType(editingType.id, formData);
        toast({
          title: 'Success',
          description: 'Book type updated successfully',
          status: 'success',
          duration: 3000,
        });
      } else {
        await api.createBookType(formData);
        toast({
          title: 'Success',
          description: 'Book type created successfully',
          status: 'success',
          duration: 3000,
        });
      }
      
      resetForm();
      onClose();
      loadData();
    } catch (error) {
      console.error('Error saving book type:', error);
      toast({
        title: 'Error',
        description: 'Failed to save book type',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (bookType: BookType) => {
    setTypeToDelete(bookType);
    onDeleteModalOpen();
  };

  const confirmDelete = async () => {
    if (!typeToDelete) return;
    
    try {
      setLoading(true);
      await api.deleteBookType(typeToDelete.id);
      toast({
        title: 'Success',
        description: 'Book type deleted successfully',
        status: 'success',
        duration: 3000,
      });
      loadData();
    } catch (error) {
      console.error('Error deleting book type:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete book type',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
      setTypeToDelete(null);
      onDeleteModalClose();
    }
  };

  const handleEdit = (bookType: BookType) => {
    setEditingType(bookType);
    setFormData({
      typeName: bookType.typeName,
      description: bookType.description || '',
      contentType: bookType.contentType
    });
    onOpen();
  };

  const resetForm = () => {
    setFormData({ 
      typeName: '', 
      description: '', 
      contentType: 'PDF' 
    });
    setEditingType(null);
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <VStack spacing={6} align="stretch">
      <HStack justify="space-between">
        <Heading size="xl" color="gray.700">
          📁 Book Types Management
        </Heading>
        <Button colorScheme="teal" onClick={() => { resetForm(); onOpen(); }}>
          ➕ Add Book Type
        </Button>
      </HStack>

      {/* Search Bar */}
      <Card bg={cardBg} shadow="sm">
        <CardBody>
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <Text>🔍</Text>
            </InputLeftElement>
            <Input
              placeholder="Search book types by name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              bg="white"
            />
          </InputGroup>
          {searchTerm && (
            <Text fontSize="sm" color="gray.500" mt={2}>
              Found {filteredBookTypes.length} of {bookTypes.length} book types
            </Text>
          )}
        </CardBody>
      </Card>

      {/* Book Types Table */}
      <Card bg={cardBg} shadow="md">
        <CardBody>
          {loading ? (
            <Text>Loading...</Text>
          ) : filteredBookTypes.length === 0 ? (
            searchTerm ? (
              <Alert status="info">
                <AlertIcon />
                No book types found matching "{searchTerm}". Try a different search term.
              </Alert>
            ) : (
              <Alert status="info">
                <AlertIcon />
                No book types found. Create your first book type!
              </Alert>
            )
          ) : (
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Type Name</Th>
                  <Th>Content Type</Th>
                  <Th>Description</Th>
                  <Th>Files Count</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredBookTypes.map((bookType) => (
                  <Tr key={bookType.id}>
                    <Td>
                      <Badge colorScheme="teal" size="md" p={2}>
                        {bookType.typeName}
                      </Badge>
                    </Td>
                    <Td>
                      <HStack>
                        <Text>{getContentTypeIcon(bookType.contentType)}</Text>
                        <Badge colorScheme="purple">
                          {bookType.contentType}
                        </Badge>
                      </HStack>
                    </Td>
                    <Td maxW="300px">
                      <Text fontSize="sm" color="gray.600">
                        {bookType.description || 'No description provided'}
                      </Text>
                    </Td>
                    <Td>
                      <Badge colorScheme="blue">
                        <HStack spacing={1}>
                          <Text>{bookType._count?.bookFiles || 0}</Text>
                          <Text fontSize="sm">
                            {(bookType._count?.bookFiles || 0) === 1 ? 'file' : 'files'}
                          </Text>
                        </HStack>
                      </Badge>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <IconButton
                          icon={<Text>✏️</Text>}
                          aria-label="Edit"
                          size="sm"
                          colorScheme="blue"
                          variant="ghost"
                          onClick={() => handleEdit(bookType)}
                        />
                        <IconButton
                          icon={<Text>🗑️</Text>}
                          aria-label="Delete"
                          size="sm"
                          colorScheme="red"
                          variant="ghost"
                          onClick={() => handleDelete(bookType)}
                        />
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={onDeleteModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>⚠️ Confirm Deletion</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text>
                Are you sure you want to delete the book type "{typeToDelete?.typeName}"?
              </Text>
              <Alert status="warning">
                <AlertIcon />
                <Text>
                  Deleting this type will permanently delete all files associated with it. This action cannot be undone.
                </Text>
              </Alert>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteModalClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="red" 
              onClick={confirmDelete}
              isLoading={loading}
            >
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingType ? 'Edit Book Type' : 'Create New Book Type'}
          </ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Type Name</FormLabel>
                  <Input
                    value={formData.typeName}
                    onChange={(e) => setFormData({ ...formData, typeName: e.target.value })}
                    placeholder="e.g., PDF, eBook, Audiobook, Video"
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Content Type</FormLabel>
                  <Select
                    value={formData.contentType}
                    onChange={(e) => setFormData({ ...formData, contentType: e.target.value as ContentType })}
                  >
                    {Object.entries(CONTENT_TYPES).map(([key, value]) => (
                      <option key={key} value={value}>
                        {getContentTypeIcon(value)} {value}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe this book type and its purpose..."
                    rows={3}
                  />
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" colorScheme="teal" isLoading={loading}>
                {editingType ? 'Update' : 'Create'}
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default BookTypesPage; 